import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BuildingLibraryIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  ScaleIcon,
  SignalIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { fiscalAPI } from '../services/api';
import {
  Button,
  Card,
  Input,
  LoadingSpinner,
  ProtectedRoute,
  ConfirmModal,
} from '../components/ui';
import { usePermissions } from '../hooks/usePermissions';
import FiscalDashboardHeader from '../components/fiscal/FiscalDashboardHeader';
import FiscalReadiness from '../components/fiscal/FiscalReadiness';
import FiscalStatusBadge from '../components/fiscal/FiscalStatusBadge';

type TabId =
  | 'general'
  | 'sequences'
  | 'billing'
  | 'taxes'
  | 'electronic'
  | 'diagnostics';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'general', label: 'General', icon: BuildingLibraryIcon },
  { id: 'sequences', label: 'Comprobantes e-CF', icon: DocumentTextIcon },
  { id: 'billing', label: 'Facturación mensual', icon: CalendarDaysIcon },
  { id: 'taxes', label: 'Impuestos', icon: ScaleIcon },
  { id: 'electronic', label: 'Facturación electrónica', icon: SignalIcon },
  { id: 'diagnostics', label: 'Diagnóstico', icon: ShieldCheckIcon },
];

const TAXPAYER_OPTIONS = [
  { value: 'PERSONA_FISICA', label: 'Persona física' },
  { value: 'PERSONA_JURIDICA', label: 'Persona jurídica' },
  { value: 'REGIMEN_ESPECIAL', label: 'Régimen especial' },
  { value: 'GUBERNAMENTAL', label: 'Gubernamental' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const FiscalCenter: React.FC = () => {
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabId>('general');
  const [confirm, setConfirm] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const { data: dashRes, isLoading: loadingDash } = useQuery(
    'fiscal-dashboard',
    fiscalAPI.getDashboard,
    { staleTime: 15_000 }
  );
  const dashboard = dashRes?.data?.data;

  const { data: profileRes, isLoading: loadingProfile } = useQuery(
    'fiscal-profile',
    fiscalAPI.getProfile,
    { enabled: tab === 'general' }
  );
  const profile = profileRes?.data?.data;

  const { data: seqRes, isLoading: loadingSeq, refetch: refetchSeq } = useQuery(
    'fiscal-sequences',
    fiscalAPI.getSequences,
    { enabled: tab === 'sequences' || tab === 'diagnostics' }
  );
  const sequences = seqRes?.data?.data || [];

  const { data: billingRes, isLoading: loadingBilling } = useQuery(
    'fiscal-billing',
    fiscalAPI.getBilling,
    { enabled: tab === 'billing' }
  );
  const billing = billingRes?.data?.data;

  const { data: taxesRes, isLoading: loadingTaxes, refetch: refetchTaxes } = useQuery(
    'fiscal-taxes',
    fiscalAPI.getTaxes,
    { enabled: tab === 'taxes' }
  );
  const taxes = taxesRes?.data?.data || [];

  const { data: electronicRes, isLoading: loadingElectronic, refetch: refetchElectronic } =
    useQuery('fiscal-electronic', fiscalAPI.getElectronic, {
      enabled: tab === 'electronic',
    });
  const electronic = electronicRes?.data?.data;

  const { data: readinessRes, isLoading: loadingReadiness, refetch: refetchReadiness } = useQuery(
    'fiscal-readiness',
    fiscalAPI.getReadiness,
    { enabled: tab === 'diagnostics', staleTime: 0 }
  );

  React.useEffect(() => {
    if (readinessRes?.data?.data) setReadiness(readinessRes.data.data);
  }, [readinessRes]);

  const readinessMutation = useMutation(fiscalAPI.getReadiness, {
    onSuccess: (res) => {
      setReadiness(res.data?.data);
      queryClient.invalidateQueries('fiscal-dashboard');
      queryClient.invalidateQueries('fiscal-readiness');
      toast.success('Diagnóstico actualizado');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Error en diagnóstico'),
  });

  const [profileForm, setProfileForm] = useState<any>(null);
  React.useEffect(() => {
    if (profile) setProfileForm({ ...profile });
  }, [profile]);

  const [billingForm, setBillingForm] = useState<any>(null);
  React.useEffect(() => {
    if (billing) setBillingForm({ ...billing });
  }, [billing]);

  const saveProfile = useMutation(fiscalAPI.updateProfile, {
    onSuccess: () => {
      queryClient.invalidateQueries('fiscal-profile');
      queryClient.invalidateQueries('fiscal-dashboard');
      toast.success('Datos fiscales guardados');
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Error al guardar perfil fiscal'),
  });

  const saveBilling = useMutation(fiscalAPI.updateBilling, {
    onSuccess: () => {
      queryClient.invalidateQueries('fiscal-billing');
      queryClient.invalidateQueries('configuration');
      toast.success('Facturación mensual actualizada');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Error al guardar'),
  });

  const toggleSeq = useMutation(
    ({ ecfType, enable }: { ecfType: string; enable: boolean }) =>
      enable ? fiscalAPI.enableSequence(ecfType) : fiscalAPI.disableSequence(ecfType),
    {
      onSuccess: () => {
        refetchSeq();
        queryClient.invalidateQueries('fiscal-dashboard');
        toast.success('Secuencia actualizada');
      },
      onError: (e: any) => toast.error(e.response?.data?.message || 'Error'),
    }
  );

  const updateAuth = useMutation(
    ({ ecfType, data }: { ecfType: string; data: any }) =>
      fiscalAPI.updateSequenceAuthorization(ecfType, data),
    {
      onSuccess: () => {
        refetchSeq();
        toast.success('Rango de autorización guardado');
      },
      onError: (e: any) => toast.error(e.response?.data?.message || 'Error al guardar rango'),
    }
  );

  const { data: docsRes, refetch: refetchDocs } = useQuery(
    'fiscal-documents',
    () => fiscalAPI.listDocuments({ limit: 15 }),
    { enabled: tab === 'electronic' || tab === 'diagnostics' }
  );
  const documents = docsRes?.data?.data || [];

  const refreshDoc = useMutation(
    (id: string) => fiscalAPI.refreshDocumentStatus(id),
    {
      onSuccess: () => {
        refetchDocs();
        queryClient.invalidateQueries('fiscal-dashboard');
        toast.success('Estado DGII actualizado');
      },
      onError: (e: any) => toast.error(e.response?.data?.message || 'Error al consultar TrackId'),
    }
  );

  const saveElectronic = useMutation(fiscalAPI.updateElectronic, {
    onSuccess: () => {
      refetchElectronic();
      queryClient.invalidateQueries('fiscal-dashboard');
      toast.success('Configuración electrónica guardada');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Error'),
  });

  const [certForm, setCertForm] = useState({
    certificateName: '',
    validUntil: '',
    password: '',
    file: null as File | null,
  });

  const uploadCert = useMutation(
    (fd: FormData) => fiscalAPI.uploadCertificate(fd),
    {
      onSuccess: () => {
        refetchElectronic();
        queryClient.invalidateQueries('fiscal-dashboard');
        setCertForm({ certificateName: '', validUntil: '', password: '', file: null });
        toast.success('Certificado reemplazado');
      },
      onError: (e: any) => toast.error(e.response?.data?.message || 'Error al subir certificado'),
    }
  );

  const [taxForm, setTaxForm] = useState({
    code: '',
    name: '',
    taxType: 'ITBIS',
    taxRate: 18,
    isTaxExempt: false,
    priceIncludesTax: false,
  });

  const createTax = useMutation(fiscalAPI.createTax, {
    onSuccess: () => {
      refetchTaxes();
      setTaxForm({
        code: '',
        name: '',
        taxType: 'ITBIS',
        taxRate: 18,
        isTaxExempt: false,
        priceIncludesTax: false,
      });
      toast.success('Perfil tributario creado');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Error'),
  });

  const catalog = useMemo(() => dashboard?.ecfTypesCatalog || [], [dashboard]);

  const requestDisableSequence = (ecfType: string) => {
    setConfirm({
      isOpen: true,
      title: 'Desactivar comprobante',
      message: `¿Desactivar el tipo ${ecfType}? No se podrán emitir nuevos e-NCF de este tipo hasta reactivarlo.`,
      onConfirm: () => {
        toggleSeq.mutate({ ecfType, enable: false });
        setConfirm((p) => ({ ...p, isOpen: false }));
      },
    });
  };

  const requestProduction = () => {
    setConfirm({
      isOpen: true,
      title: 'Cambiar a Producción',
      message:
        'Está a punto de cambiar el ambiente DGII a Producción. Solo use esto con certificados y autorización reales. ¿Continuar?',
      onConfirm: () => {
        saveElectronic.mutate({ environment: 'PRODUCTION' });
        setConfirm((p) => ({ ...p, isOpen: false }));
      },
    });
  };

  const requestCertUpload = () => {
    setConfirm({
      isOpen: true,
      title: 'Reemplazar certificado',
      message:
        'Se desactivará el certificado actual y se cargará uno nuevo. La contraseña no se podrá recuperar después. ¿Continuar?',
      onConfirm: () => {
        const fd = new FormData();
        if (certForm.file) fd.append('certificate', certForm.file);
        if (certForm.certificateName) fd.append('certificateName', certForm.certificateName);
        if (certForm.validUntil) fd.append('validUntil', certForm.validUntil);
        if (certForm.password) fd.append('password', certForm.password);
        uploadCert.mutate(fd);
        setConfirm((p) => ({ ...p, isOpen: false }));
      },
    });
  };

  if (!isAdmin) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-900">Acceso denegado</h2>
          <p className="text-slate-600">Solo administradores gestionan el Centro Fiscal.</p>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute permission="configuration.read">
      <div className="space-y-6 animate-fade-in-up">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Link
              to="/configuration"
              className="mb-2 inline-flex items-center text-sm text-slate-500 hover:text-slate-800"
            >
              <ArrowLeftIcon className="mr-1 h-4 w-4" />
              Volver a configuración
            </Link>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              Centro Fiscal DGII
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Datos fiscales, secuencias e-NCF, impuestos y preparación para facturación electrónica.
            </p>
          </div>
        </div>

        <FiscalDashboardHeader data={dashboard} loading={loadingDash} />

        {/* Tabs: scroll horizontal en mobile */}
        <div className="-mx-1 overflow-x-auto pb-1">
          <div className="flex min-w-max gap-1 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    active
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {tab === 'general' && (
          <Card className="p-6">
            {loadingProfile || !profileForm ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Datos fiscales</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input
                    label="RNC / Cédula"
                    value={profileForm.rnc || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, rnc: v }))}
                    placeholder="9 u 11 dígitos"
                  />
                  <Input
                    label="Razón social"
                    value={profileForm.legalName || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, legalName: v }))}
                  />
                  <Input
                    label="Nombre comercial"
                    value={profileForm.tradeName || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, tradeName: v }))}
                  />
                  <Input
                    label="Actividad económica"
                    value={profileForm.economicActivity || ''}
                    onChange={(v) =>
                      setProfileForm((p: any) => ({ ...p, economicActivity: v }))
                    }
                  />
                  <Input
                    label="Dirección fiscal"
                    value={profileForm.fiscalAddress || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, fiscalAddress: v }))}
                  />
                  <Input
                    label="Provincia"
                    value={profileForm.province || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, province: v }))}
                  />
                  <Input
                    label="Municipio"
                    value={profileForm.municipality || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, municipality: v }))}
                  />
                  <Input
                    label="Teléfono"
                    value={profileForm.phone || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, phone: v }))}
                  />
                  <Input
                    label="Correo fiscal"
                    type="email"
                    value={profileForm.email || ''}
                    onChange={(v) => setProfileForm((p: any) => ({ ...p, email: v }))}
                  />
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-800">
                      Tipo de contribuyente
                    </label>
                    <select
                      className="w-full rounded-xl border border-slate-300 px-4 py-3"
                      value={profileForm.taxpayerType || 'PERSONA_JURIDICA'}
                      onChange={(e) =>
                        setProfileForm((p: any) => ({ ...p, taxpayerType: e.target.value }))
                      }
                    >
                      {TAXPAYER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    checked={Boolean(profileForm.isAuthorizedElectronicIssuer)}
                    onChange={(e) =>
                      setProfileForm((p: any) => ({
                        ...p,
                        isAuthorizedElectronicIssuer: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm text-slate-700">
                    Emisor electrónico autorizado (DGII)
                  </span>
                </label>
                <Button
                  type="button"
                  variant="primary"
                  loading={saveProfile.isLoading}
                  onClick={() =>
                    saveProfile.mutate({
                      rnc: profileForm.rnc,
                      legalName: profileForm.legalName,
                      tradeName: profileForm.tradeName,
                      fiscalAddress: profileForm.fiscalAddress,
                      province: profileForm.province,
                      municipality: profileForm.municipality,
                      phone: profileForm.phone,
                      email: profileForm.email,
                      economicActivity: profileForm.economicActivity,
                      taxpayerType: profileForm.taxpayerType,
                      isAuthorizedElectronicIssuer: profileForm.isAuthorizedElectronicIssuer,
                    })
                  }
                >
                  Guardar datos fiscales
                </Button>
              </div>
            )}
          </Card>
        )}

        {tab === 'sequences' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Formato e-NCF: E + tipo (2) + secuencial (10). Rangos de autorización DGII. La
              secuencia solo se consume en el backend.
            </p>
            {loadingSeq ? (
              <LoadingSpinner />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {sequences.map((s: any) => {
                  const meta = catalog.find((c: any) => c.code === s.ecfType);
                  return (
                    <Card key={s.id} className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {meta?.label || s.ecfType}
                          </p>
                          <p className="font-mono text-lg text-slate-800">{s.ecfType}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <FiscalStatusBadge
                            status={s.isEnabled ? 'ready' : 'critical'}
                            label={s.isEnabled ? 'Activo' : 'Inactivo'}
                          />
                          {s.rangeStatus && s.rangeStatus !== 'ok' && (
                            <FiscalStatusBadge
                              status={
                                s.rangeStatus === 'exhausted' || s.rangeStatus === 'expired'
                                  ? 'critical'
                                  : 'attention'
                              }
                              label={
                                s.rangeStatus === 'exhausted'
                                  ? 'Agotada'
                                  : s.rangeStatus === 'expired'
                                    ? 'Vencida'
                                    : 'Por agotar'
                              }
                            />
                          )}
                        </div>
                      </div>
                      <dl className="mt-3 space-y-1 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-slate-500">Último</dt>
                          <dd className="font-mono text-slate-800">{s.lastEncf || '—'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-500">Próximo</dt>
                          <dd className="font-mono text-slate-800">{s.nextEncf || '—'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-slate-500">Rango</dt>
                          <dd className="font-mono text-xs text-slate-700">
                            {s.authorizedFrom}–{s.authorizedTo}
                          </dd>
                        </div>
                      </dl>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <input
                          id={`auth-from-${s.ecfType}`}
                          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono"
                          defaultValue={s.authorizedFrom}
                          aria-label={`Rango desde ${s.ecfType}`}
                        />
                        <input
                          id={`auth-to-${s.ecfType}`}
                          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-mono"
                          defaultValue={s.authorizedTo}
                          aria-label={`Rango hasta ${s.ecfType}`}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {s.isEnabled ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => requestDisableSequence(s.ecfType)}
                          >
                            Desactivar
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => toggleSeq.mutate({ ecfType: s.ecfType, enable: true })}
                          >
                            Activar
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          loading={updateAuth.isLoading}
                          onClick={() => {
                            const fromEl = (
                              document.getElementById(`auth-from-${s.ecfType}`) as HTMLInputElement
                            )?.value;
                            const toEl = (
                              document.getElementById(`auth-to-${s.ecfType}`) as HTMLInputElement
                            )?.value;
                            updateAuth.mutate({
                              ecfType: s.ecfType,
                              data: {
                                authorizedFrom: fromEl || s.authorizedFrom,
                                authorizedTo: toEl || s.authorizedTo,
                              },
                            });
                          }}
                        >
                          Guardar rango
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'billing' && (
          <Card className="p-6">
            {loadingBilling || !billingForm ? (
              <LoadingSpinner />
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">Facturación mensual</h2>
                <p className="text-sm text-slate-500">
                  Plan → Contrato → Cuota → Factura → e-CF → Pago. Una cuota no es una factura; los
                  pagos se aplican a facturas existentes.
                </p>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={Boolean(billingForm.autoGenerateInvoices)}
                    onChange={(e) =>
                      setBillingForm((p: any) => ({
                        ...p,
                        autoGenerateInvoices: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm">Generar facturas automáticamente</span>
                </label>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-bold">Día del mes</label>
                    <select
                      className="w-full rounded-xl border border-slate-300 px-4 py-3"
                      value={billingForm.billingDay}
                      onChange={(e) =>
                        setBillingForm((p: any) => ({
                          ...p,
                          billingDay: parseInt(e.target.value, 10),
                        }))
                      }
                    >
                      {DAYS.map((d) => (
                        <option key={d} value={d}>
                          Día {d}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-bold">Hora</label>
                    <select
                      className="w-full rounded-xl border border-slate-300 px-4 py-3"
                      value={billingForm.billingHour}
                      onChange={(e) =>
                        setBillingForm((p: any) => ({
                          ...p,
                          billingHour: parseInt(e.target.value, 10),
                        }))
                      }
                    >
                      {HOURS.map((h) => (
                        <option key={h} value={h}>
                          {String(h).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Zona horaria"
                    value={billingForm.billingTimezone || ''}
                    onChange={(v) => setBillingForm((p: any) => ({ ...p, billingTimezone: v }))}
                    placeholder="America/Santo_Domingo"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-bold">Tipo e-CF predeterminado</label>
                    <select
                      className="w-full rounded-xl border border-slate-300 px-4 py-3"
                      value={billingForm.defaultEcfType || 'E32'}
                      onChange={(e) =>
                        setBillingForm((p: any) => ({ ...p, defaultEcfType: e.target.value }))
                      }
                    >
                      {(catalog.length ? catalog : [{ code: 'E32', label: 'Consumo' }]).map(
                        (c: any) => (
                          <option key={c.code} value={c.code}>
                            {c.code} — {c.label}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <Input
                    label="Plantilla de descripción"
                    value={billingForm.defaultDescriptionTemplate || ''}
                    onChange={(v) =>
                      setBillingForm((p: any) => ({ ...p, defaultDescriptionTemplate: v }))
                    }
                    placeholder="Cuota correspondiente a {month} {year}"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={Boolean(billingForm.autoIssueEcf)}
                      onChange={(e) =>
                        setBillingForm((p: any) => ({ ...p, autoIssueEcf: e.target.checked }))
                      }
                    />
                    <span className="text-sm">Auto-emitir e-CF (stub / futuro)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={Boolean(billingForm.autoSendEmail)}
                      onChange={(e) =>
                        setBillingForm((p: any) => ({ ...p, autoSendEmail: e.target.checked }))
                      }
                    />
                    <span className="text-sm">Enviar correo al generar</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={Boolean(billingForm.allowBillingWithPreviousDebt)}
                      onChange={(e) =>
                        setBillingForm((p: any) => ({
                          ...p,
                          allowBillingWithPreviousDebt: e.target.checked,
                        }))
                      }
                    />
                    <span className="text-sm">Permitir facturar con deuda previa</span>
                  </label>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  loading={saveBilling.isLoading}
                  onClick={() => saveBilling.mutate(billingForm)}
                >
                  Guardar facturación mensual
                </Button>
              </div>
            )}
          </Card>
        )}

        {tab === 'taxes' && (
          <div className="space-y-4">
            <Card className="p-6">
              <h2 className="mb-3 text-lg font-semibold">Perfiles tributarios</h2>
              {loadingTaxes ? (
                <LoadingSpinner />
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {taxes.map((t: any) => (
                    <div
                      key={t.id}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-slate-900">
                          {t.name}{' '}
                          <span className="font-mono text-xs text-slate-500">{t.code}</span>
                        </p>
                        <FiscalStatusBadge
                          status={t.isActive ? 'ready' : 'critical'}
                          label={t.isActive ? 'Activo' : 'Inactivo'}
                        />
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        {t.taxType} · {Number(t.taxRate)}%
                        {t.isTaxExempt ? ' · Exento' : ''}
                        {t.priceIncludesTax ? ' · Precio incluye impuesto' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-6">
              <h3 className="mb-3 font-semibold">Nuevo perfil</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Input
                  label="Código"
                  value={taxForm.code}
                  onChange={(v) => setTaxForm((p) => ({ ...p, code: v }))}
                />
                <Input
                  label="Nombre"
                  value={taxForm.name}
                  onChange={(v) => setTaxForm((p) => ({ ...p, name: v }))}
                />
                <div>
                  <label className="mb-2 block text-sm font-bold">Tipo</label>
                  <select
                    className="w-full rounded-xl border border-slate-300 px-4 py-3"
                    value={taxForm.taxType}
                    onChange={(e) => setTaxForm((p) => ({ ...p, taxType: e.target.value }))}
                  >
                    <option value="ITBIS">ITBIS</option>
                    <option value="EXEMPT">Exento</option>
                    <option value="RETENTION">Retención</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </div>
                <Input
                  label="Tasa %"
                  type="number"
                  value={String(taxForm.taxRate)}
                  onChange={(v) => setTaxForm((p) => ({ ...p, taxRate: Number(v) || 0 }))}
                />
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={taxForm.isTaxExempt}
                    onChange={(e) => setTaxForm((p) => ({ ...p, isTaxExempt: e.target.checked }))}
                  />
                  Exento
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={taxForm.priceIncludesTax}
                    onChange={(e) =>
                      setTaxForm((p) => ({ ...p, priceIncludesTax: e.target.checked }))
                    }
                  />
                  Precio incluye impuesto
                </label>
              </div>
              <Button
                className="mt-4"
                type="button"
                variant="primary"
                loading={createTax.isLoading}
                onClick={() => createTax.mutate(taxForm)}
              >
                Crear perfil
              </Button>
            </Card>
          </div>
        )}

        {tab === 'electronic' && (
          <div className="space-y-4">
            <Card className="p-6">
              {loadingElectronic ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold">Facturación electrónica DGII</h2>
                    <FiscalStatusBadge status={electronic?.settings?.environment} />
                  </div>
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded"
                      checked={Boolean(electronic?.settings?.electronicBillingEnabled)}
                      onChange={(e) =>
                        saveElectronic.mutate({
                          electronicBillingEnabled: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm">Habilitar facturación electrónica</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant={
                        electronic?.settings?.environment === 'TEST' ? 'primary' : 'outline'
                      }
                      size="sm"
                      onClick={() => saveElectronic.mutate({ environment: 'TEST' })}
                    >
                      TesteCF
                    </Button>
                    <Button
                      type="button"
                      variant={
                        electronic?.settings?.environment === 'CERTIFICATION'
                          ? 'primary'
                          : 'outline'
                      }
                      size="sm"
                      onClick={() => saveElectronic.mutate({ environment: 'CERTIFICATION' })}
                    >
                      Certificación
                    </Button>
                    <Button
                      type="button"
                      variant={
                        electronic?.settings?.environment === 'PRODUCTION'
                          ? 'primary'
                          : 'outline'
                      }
                      size="sm"
                      onClick={requestProduction}
                    >
                      Producción
                    </Button>
                  </div>
                  {electronic?.certificate ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{electronic.certificate.certificateName}</p>
                        <FiscalStatusBadge status={electronic.certificate.validityStatus} />
                      </div>
                      <p className="mt-1 text-sm text-slate-600">
                        Vence:{' '}
                        {electronic.certificate.validUntil
                          ? new Date(electronic.certificate.validUntil).toLocaleDateString('es-DO')
                          : '—'}
                        {electronic.certificate.daysRemaining != null &&
                          ` · ${electronic.certificate.daysRemaining} días restantes`}
                      </p>
                      <p className="text-xs text-slate-500">
                        Contraseña: {electronic.certificate.hasPassword ? 'configurada (oculta)' : 'no definida'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-700">Sin certificado activo.</p>
                  )}
                </div>
              )}
            </Card>
            <Card className="p-6">
              <h3 className="mb-3 font-semibold">Cargar / reemplazar certificado</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Input
                  label="Nombre"
                  value={certForm.certificateName}
                  onChange={(v) => setCertForm((p) => ({ ...p, certificateName: v }))}
                />
                <Input
                  label="Válido hasta (opcional si el P12 lo trae)"
                  type="date"
                  value={certForm.validUntil}
                  onChange={(v) => setCertForm((p) => ({ ...p, validUntil: v }))}
                />
                <Input
                  label="Contraseña (solo escritura)"
                  type="password"
                  value={certForm.password}
                  onChange={(v) => setCertForm((p) => ({ ...p, password: v }))}
                  placeholder="No se podrá recuperar"
                />
                <div>
                  <label className="mb-2 block text-sm font-bold">Archivo (.p12 / .pfx)</label>
                  <input
                    type="file"
                    accept=".p12,.pfx,.pem,.crt"
                    onChange={(e) =>
                      setCertForm((p) => ({ ...p, file: e.target.files?.[0] || null }))
                    }
                  />
                </div>
              </div>
              <Button
                className="mt-4"
                type="button"
                variant="primary"
                loading={uploadCert.isLoading}
                onClick={requestCertUpload}
              >
                Reemplazar certificado
              </Button>
            </Card>

            <Card className="p-6">
              <h3 className="mb-3 font-semibold">Documentos e-CF recientes</h3>
              {documents.length === 0 ? (
                <p className="text-sm text-slate-500">Aún no hay emisiones electrónicas.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {documents.map((d: any) => (
                    <li
                      key={d.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-3"
                    >
                      <div>
                        <p className="font-mono text-sm font-semibold">{d.encf}</p>
                        <p className="text-xs text-slate-500">
                          {d.ecfType} · TrackId: {d.trackId || '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <FiscalStatusBadge
                          status={
                            d.dgiiStatus === 'ACCEPTED'
                              ? 'ready'
                              : d.dgiiStatus === 'REJECTED' || d.dgiiStatus === 'ERROR'
                                ? 'critical'
                                : 'attention'
                          }
                          label={d.dgiiStatus}
                        />
                        {d.trackId &&
                          !['ACCEPTED', 'CONDITIONALLY_ACCEPTED', 'REJECTED', 'CANCELLED'].includes(
                            d.dgiiStatus
                          ) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              loading={refreshDoc.isLoading}
                              onClick={() => refreshDoc.mutate(d.id)}
                            >
                              Actualizar estado
                            </Button>
                          )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        {tab === 'diagnostics' && (
          <Card className="p-6">
            <FiscalReadiness
              data={readiness || readinessRes?.data?.data}
              loading={readinessMutation.isLoading || loadingReadiness}
              onRun={() => readinessMutation.mutate()}
            />
          </Card>
        )}

        <ConfirmModal
          isOpen={confirm.isOpen}
          title={confirm.title}
          message={confirm.message}
          type="warning"
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm((p) => ({ ...p, isOpen: false }))}
        />
      </div>
    </ProtectedRoute>
  );
};

export default FiscalCenter;
